import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BarChartWrapper from "components/Dashboard/BarChartWrapper";
import {
  computeMixedInnerTooltipBreakdown,
  resolveMixedInnerTooltipSource,
} from "util/intentMixedInnerTooltip";

/**
 * Welcome metric 12 payload → rows for dashboard BarChartWrapper (same chart chrome as metric 14).
 */
export function welcomeMetric12StackedBarToBarChartData(tp) {
  if (!tp || tp.chart_type !== "stacked_bar") {
    return { rows: [], segmentOrder: [] };
  }
  const labels = tp.labels || [];
  if (!labels.length) {
    return { rows: [], segmentOrder: [] };
  }
  const datasets = tp.datasets || [];
  const segmentOrder = datasets
    .map((d) => d?.label)
    .filter((lab) => lab != null && String(lab).trim() !== "");
  const counts = Array.isArray(tp.topic_review_counts)
    ? tp.topic_review_counts
    : null;
  const mixedInnerTooltipRows = Array.isArray(tp.mixed_inner_tooltip)
    ? tp.mixed_inner_tooltip
    : null;

  const rows = labels.map((label, i) => {
    const intent = {};
    for (const ds of datasets) {
      const key = ds?.label;
      if (key == null || String(key).trim() === "") continue;
      const vals = Array.isArray(ds.data) ? ds.data : [];
      intent[key] = { percentage: vals[i] ?? 0 };
    }
    const mixedSource = resolveMixedInnerTooltipSource(
      mixedInnerTooltipRows?.[i],
    );
    const innerBreakdown = computeMixedInnerTooltipBreakdown(
      mixedSource.mixedReviewCount,
      mixedSource.intents,
    );
    if (innerBreakdown.length > 0) {
      const mixedKey =
        Object.keys(intent).find((k) => String(k).toLowerCase() === "mixed") ||
        "mixed";
      intent[mixedKey] = {
        ...(intent[mixedKey] || {}),
        innerBreakdown,
      };
    }
    return {
      label,
      id: `welcome-intent-${i}`,
      child: null,
      count: counts && counts[i] != null ? Number(counts[i]) : 0,
      intent,
    };
  });

  const hasComparableCounts = rows.some((r) => r.count > 0);
  if (hasComparableCounts) {
    rows.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return String(a.label).localeCompare(String(b.label), undefined, {
        sensitivity: "base",
      });
    });
  }

  return {
    segmentOrder,
    rows: rows.map((row, j) => ({
      ...row,
      id: `welcome-intent-${j}`,
    })),
  };
}

/**
 * Intent Distribution (metric 12) — same BarChartWrapper styling as Sentiment per Topic (metric 14).
 */
export default function WelcomeTopicIntentBarChart({ payload }) {
  const { t } = useTranslation();
  const { rows, segmentOrder } = useMemo(
    () => welcomeMetric12StackedBarToBarChartData(payload),
    [payload],
  );
  const [selectedTopics, setSelectedTopics] = useState({});

  const hasIntentValues = useMemo(
    () =>
      rows.some((row) =>
        Object.values(row.intent || {}).some(
          (seg) => Number(seg?.percentage ?? 0) > 0,
        ),
      ),
    [rows],
  );

  if (!rows.length) return null;

  if (!hasIntentValues) {
    return (
      <div style={{ color: "#888", fontSize: 13, padding: "12px 0" }}>
        {t("welcome.39")}
      </div>
    );
  }

  return (
    <BarChartWrapper
      data={rows}
      selectedTopics={selectedTopics}
      setSelectedTopics={setSelectedTopics}
      metric="intent"
      stacked={true}
      disableSelection={true}
      onlyOneMetric={false}
      scrollable={true}
      intentSegmentOrder={segmentOrder}
    />
  );
}
